<template>
  <div class="w-full">
    <!-- Skeleton Loading State -->
    <div
      v-if="showSkeleton"
      class="animate-pulse">
      <!-- Search skeleton -->
      <div class="bg-white border border-gray-200 rounded-lg shadow-sm mb-6 p-4">
        <div class="h-10 bg-gray-200 rounded w-full max-w-md" />
      </div>

      <!-- Table skeleton -->
      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-4 space-y-3">
          <div
            v-for="i in 5"
            :key="i"
            class="flex space-x-4">
            <div class="h-4 bg-gray-200 rounded w-1/4" />
            <div class="h-4 bg-gray-200 rounded w-1/3" />
            <div class="h-4 bg-gray-200 rounded w-1/4" />
            <div class="h-4 bg-gray-200 rounded w-1/6" />
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content (hidden during skeleton loading) -->
    <div v-show="!showSkeleton">
      <!-- Search and Filter Controls -->
      <div class="bg-white border border-gray-200 rounded-lg shadow-sm mb-6 p-4">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Search Input -->
          <div class="flex-1 min-w-[300px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              <i class="fas fa-search text-gray-400 mr-1" />
              Search Customers
            </label>
            <input
              v-model="searchQuery"
              type="text"
              class="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                   focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors duration-200"
              placeholder="Search by name, email, or website..."
              @input="handleSearch">
          </div>

          <!-- Filter Toggles -->
          <div class="flex items-center gap-4">
            <label class="flex items-center cursor-pointer">
              <input
                v-model="filters.showInactive"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500">
              <span class="ml-2 text-sm text-gray-700">Show inactive</span>
            </label>

            <label class="flex items-center cursor-pointer">
              <input
                v-model="filters.spotlightOnly"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500">
              <span class="ml-2 text-sm text-gray-700">Spotlight only</span>
            </label>

            <label class="flex items-center cursor-pointer">
              <input
                v-model="filters.geogridOnly"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500">
              <span class="ml-2 text-sm text-gray-700">GeoGrid only</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div
        v-if="error"
        class="bg-white rounded-lg shadow-sm p-6">
        <div class="bg-red-50 border-l-4 border-red-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="fas fa-exclamation-triangle text-red-400" />
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-800">
                {{ error }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Organizations table -->
      <div
        v-else-if="filteredOrganizations.length > 0"
        class="bg-white rounded-lg shadow-sm overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="w-12 px-6 py-3" />
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer Details
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Locations
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Spotlight
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                GeoGrid
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template
              v-for="org in filteredOrganizations"
              :key="org.id">
              <!-- Main Row -->
              <tr
                class="hover:bg-gray-50 transition-colors cursor-pointer"
                :class="{ 'bg-blue-50': expandedRows[org.id] }"
                @click="toggleRow(org.id)">
                <!-- Expand/Collapse Icon -->
                <td class="px-6 py-4">
                  <button
                    class="text-gray-400 hover:text-gray-600 transition-colors"
                    @click.stop="toggleRow(org.id)">
                    <i :class="expandedRows[org.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" />
                  </button>
                </td>

                <!-- Customer Details Cell -->
                <td class="px-6 py-4">
                  <div class="flex flex-col">
                    <div class="text-sm font-medium text-gray-900">
                      {{ org.name }}
                    </div>
                    <div
                      v-if="org.website_url"
                      class="text-xs text-blue-600 mt-1">
                      <i class="fas fa-globe mr-1" />
                      <a
                        :href="org.website_url"
                        target="_blank"
                        class="hover:underline"
                        @click.stop>
                        {{ org.website_url }}
                      </a>
                    </div>
                  </div>
                </td>

                <!-- Locations -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <i class="fas fa-map-marker-alt text-gray-400 mr-2" />
                    <span class="text-sm text-gray-900">{{ org.locations_count || 0 }}</span>
                  </div>
                </td>

                <!-- Spotlight Status -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    v-if="org.has_spotlight"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <i class="fas fa-check-circle mr-1" />
                    Enabled
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <i class="fas fa-times-circle mr-1" />
                    Disabled
                  </span>
                  <div
                    v-if="org.spotlight_tracking"
                    class="text-xs text-gray-500 mt-1">
                    {{ org.spotlight_tracking.active_pages || 0 }} active pages
                  </div>
                </td>

                <!-- GeoGrid Status -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    v-if="org.has_geogrid"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <i class="fas fa-check-circle mr-1" />
                    Enabled
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <i class="fas fa-times-circle mr-1" />
                    Disabled
                  </span>
                  <div
                    v-if="org.geogrid_status"
                    class="text-xs text-gray-500 mt-1">
                    {{ org.geogrid_status.total_locations || 0 }} configured
                  </div>
                </td>

                <!-- Status -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    v-if="!org.deleted_at"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <i
                      class="fas fa-circle text-green-600 mr-1"
                      style="font-size: 8px;" />
                    Active
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <i
                      class="fas fa-circle text-red-600 mr-1"
                      style="font-size: 8px;" />
                    Inactive
                  </span>
                  <div class="text-xs text-gray-500 mt-1">
                    Since {{ formatDate(org.created_at) }}
                  </div>
                </td>

                <!-- Actions -->
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <div class="flex items-center space-x-2">
                    <button
                      class="inline-flex items-center justify-center p-2 rounded-lg border
                             border-gray-300 bg-white text-gray-700 hover:bg-gray-50
                             hover:border-gray-400 transition-all duration-200 shadow-sm"
                      :title="expandedRows[org.id] ? 'Hide Details' : 'View Details'"
                      @click.stop="toggleRow(org.id)">
                      <i
                        :class="expandedRows[org.id] ? 'fas fa-chevron-up' : 'fas fa-eye'"
                        class="text-sm" />
                    </button>
                    <button
                      class="inline-flex items-center justify-center p-2 rounded-lg border
                             border-green-300 bg-green-50 text-green-700 hover:bg-green-100
                             hover:border-green-400 transition-all duration-200 shadow-sm"
                      title="Impersonate Customer"
                      @click.stop="impersonateOrganization(org)">
                      <i class="fas fa-user-secret text-sm" />
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Expanded Row -->
              <tr
                v-if="expandedRows[org.id]"
                :key="`${org.id}-expanded`">
                <td
                  colspan="7"
                  class="p-0">
                  <ResellerOrganizationExpandedRow
                    :organization-id="org.id"
                    :organization="org" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="bg-white rounded-lg shadow-sm p-12">
        <div class="text-center">
          <i class="fas fa-building text-6xl text-gray-400 mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ searchQuery || filters.spotlightOnly || filters.geogridOnly ? 'No matching customers found' : 'No customers found' }}
          </h3>
          <p class="text-gray-600">
            {{ searchQuery || filters.spotlightOnly || filters.geogridOnly
              ? 'Try adjusting your search or filter criteria.'
              : "You don't have any customers assigned yet." }}
          </p>
        </div>
      </div>

      <!-- Results Summary -->
      <div
        v-if="organizations.length > 0"
        class="mt-4 text-sm text-gray-600 text-center">
        Showing {{ filteredOrganizations.length }} of {{ organizations.length }} customers
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import ResellerOrganizationExpandedRow from './organization_expanded_row.vue'
import { hideLoadingSpinner } from '~/helpers/hide_loader'

export default {
  name: 'ResellerOrganizationsTable',

  components: {
    ResellerOrganizationExpandedRow
  },

  props: {
    accountId: {
      type: String,
      required: true
    },
    resellerIdentifier: {
      type: String,
      required: false
    }
  },

  setup(_props) {
    const organizations = ref([])
    const loading = ref(true)
    const showSkeleton = ref(true)
    const error = ref(null)
    const expandedRows = ref({})
    const searchQuery = ref('')
    const filters = ref({
      showInactive: false,
      spotlightOnly: false,
      geogridOnly: false
    })

    const filteredOrganizations = computed(() => {
      let filtered = [...organizations.value]

      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter((org) => org.name?.toLowerCase().includes(query) ||
          org.account_manager_email?.toLowerCase().includes(query) ||
          org.website_url?.toLowerCase().includes(query))
      }

      // Apply status filter
      if (!filters.value.showInactive) {
        filtered = filtered.filter((org) => !org.deleted_at)
      }

      // Apply spotlight filter
      if (filters.value.spotlightOnly) {
        filtered = filtered.filter((org) => org.has_spotlight)
      }

      // Apply geogrid filter
      if (filters.value.geogridOnly) {
        filtered = filtered.filter((org) => org.has_geogrid)
      }

      return filtered
    })

    const toggleRow = (orgId) => {
      expandedRows.value[orgId] = !expandedRows.value[orgId]
    }

    const handleSearch = () => {
      // Reset expanded rows when searching
      expandedRows.value = {}
    }

    const fetchOrganizations = async() => {
      try {
        loading.value = true
        showSkeleton.value = true
        error.value = null

        const response = await fetch('/ajax/reseller/organizations', {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to fetch organizations')
        }

        const data = await response.json()
        organizations.value = data.organizations || []
      } catch (err) {
        console.error('Error fetching organizations:', err)
        error.value = err.message || 'Failed to load customers. Please try again.'
      } finally {
        loading.value = false
        showSkeleton.value = false
        // Hide the initial HTML loading spinner
        hideLoadingSpinner()
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const impersonateOrganization = async(org) => {
      if (!confirm(`Are you sure you want to impersonate ${org.name}? You will be logged in as their account.`)) {
        return
      }

      try {
        const response = await fetch(`/ajax/reseller/organizations/${org.id}/impersonate`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to impersonate organization')
        }

        const data = await response.json()

        // Redirect to the client dashboard
        if (data.success && data.redirect_url) {
          window.location.href = data.redirect_url
        } else {
          throw new Error('Impersonation failed')
        }
      } catch (err) {
        console.error('Error impersonating organization:', err)
        alert(`Failed to impersonate organization: ${err.message}`)
      }
    }

    onMounted(() => {
      fetchOrganizations()
    })

    return {
      organizations,
      filteredOrganizations,
      loading,
      showSkeleton,
      error,
      expandedRows,
      searchQuery,
      filters,
      toggleRow,
      handleSearch,
      formatDate,
      impersonateOrganization
    }
  }
}
</script>

<style scoped>
/* Add smooth transitions for row expansion */
tr {
  transition: background-color 0.2s ease;
}

/* Highlight expanded rows */
.bg-blue-50 {
  background-color: rgba(59, 130, 246, 0.05);
}

/* Add hover effect for action buttons */
button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}
</style>
